import { Button, Grid, GridColumn, Heading, Separator, Text, TextField } from '@teliaee/design-system';
import { observer } from 'mobx-react';
import * as React from 'react';
import { View } from '../Routes';
import useStores from '../stores/UseStores';
import { EMAIL_FIELD_ID, emailFieldHelper, getHelperErrorMessage, helperInErrorState } from '../util/FieldHelperUtil';
import { isEmailValid } from '../util/ValidationUtil';
import HiddenCaptcha, { HiddenCaptchaRef } from './HiddenCaptcha';
import NoticeWithButton from './NoticeWithButton';

export interface ForgotPasswordProps {
  inModal?: boolean;
  readOnly?: boolean;
}

const ForgotPassword = observer((props: ForgotPasswordProps): React.JSX.Element => {
  const { readOnly, inModal } = props;
  const {
    translateStore: { translate },
    forgotPasswordStore,
    languageStore: { currentLanguage },
    routeStore,
    uiStore: { hideNotices, showSuccessNotice, showErrorNoticeWithErroredFields },
  } = useStores();
  const { email, view, submitting, init: initForgotPasswordStore, setForgotPasswordModalState, setEmail } = forgotPasswordStore;
  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);
  const recaptchaRef = React.createRef<HiddenCaptchaRef>();

  const getEmailHelper = (forceIsDirty?: boolean) => emailFieldHelper(translate, email, forceIsDirty ?? hasSubmitted);

  React.useEffect(() => {
    hideNotices();
    if (!inModal) {
      initForgotPasswordStore();
    }
  }, []);

  const isInvalid = () => {
    return !isEmailValid(email);
  };

  const handleFormSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setHasSubmitted(true);
    if (isInvalid()) {
      showErrorNoticeWithErroredFields('form.error', [getEmailHelper(true)]);
      return;
    }
    const token = await recaptchaRef?.current?.getCaptchaAsync();
    forgotPasswordStore.handleSubmit(token);
  };

  const getForgotPasswordForm = () => {
    if (view === View.EMAIL_RESET_REQUESTED) {
      if (!inModal) {
        return (
          <NoticeWithButton
            text={translate('reset_password.requested_success')}
            buttonProps={{
              text: translate('ok_button_label'),
              url: routeStore.homePageWithParams,
              onClick: (event) => {
                event.preventDefault();
                routeStore.route(routeStore.homePageWithParams);
              },
            }}
          />
        );
      }
      showSuccessNotice('account.reset_password.success');
      setForgotPasswordModalState(false);
    }
    return (
      <Grid align={['center-xs']} className="text-center">
        <GridColumn width={['xs-12', 'sm-10', 'md-8', 'lg-10']}>
          <Text>
            <Heading level={1} className="h3 text-center" id="forgot-password-title">
              {translate('forgot_password.header')}
            </Heading>
            <p>{translate('forgot_password.info')}</p>
            <form onSubmit={handleFormSubmit} id="passwordReset">
              <TextField
                label={translate('forgot_password.email_label')}
                id={EMAIL_FIELD_ID}
                value={email || ''}
                onChange={(value) => setEmail(value)}
                isInvalid={helperInErrorState(getEmailHelper())}
                errorMessage={getHelperErrorMessage(getEmailHelper())}
                isReadOnly={readOnly}
              />
              <HiddenCaptcha ref={recaptchaRef} currentLanguage={currentLanguage} />
              <Separator color="invisible" spacing="small" />
              <Button text={translate('forgot_password.button_label')} onClick={handleFormSubmit} iconRight="#arrow-right" isLoading={submitting} />
            </form>
          </Text>
        </GridColumn>
      </Grid>
    );
  };

  if (!inModal) {
    return (
      <Grid align={['center-xs']}>
        <GridColumn width={['xs-12', 'lg-6']}>{getForgotPasswordForm()}</GridColumn>
      </Grid>
    );
  }

  return getForgotPasswordForm();
});

export default ForgotPassword;
